import React, { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import visibleIcon from '../Resources/Images/Icons/visible_icon.svg';
import invisibleIcon from '../Resources/Images/Icons/invisible_icon.svg';

type RegisterFormData = {
  organizationFullNameZh: string;
  organizationCode: string;
  password: string;
  confirmPassword: string;
  organizationShortNameZh: string;
  organizationShortNameEn: string;
  contactName: string;
  contactDepartment: string;
  contactTitle: string;
  contactPhone: string;
  contactExtension: string;
  contactEmail: string;
};

const REQUIRED_FIELDS: (keyof RegisterFormData)[] = [
  'organizationFullNameZh',
  'organizationCode',
  'password',
  'confirmPassword',
  'organizationShortNameZh',
  'organizationShortNameEn',
  'contactName',
  'contactDepartment',
  'contactTitle',
  'contactPhone',
  'contactEmail',
];

type BaseFieldProps = {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'password' | 'email' | 'tel';
  showRequired?: boolean;
  requiredText?: string;
};

type PasswordFieldProps = BaseFieldProps & {
  isVisible: boolean;
  onToggleVisibility: () => void;
  helperText?: string;
};

const INITIAL_FORM: RegisterFormData = {
  organizationFullNameZh: '',
  organizationCode: '',
  password: '',
  confirmPassword: '',
  organizationShortNameZh: '',
  organizationShortNameEn: '',
  contactName: '',
  contactDepartment: '',
  contactTitle: '',
  contactPhone: '',
  contactExtension: '',
  contactEmail: '',
};

const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;

const inputBaseStyles = {
  h: '45px',
  pr: '12px',
  border: '1px solid #A8B8C2',
  borderRadius: '3px',
  bg: 'transparent',
  color: '#0A0A25',
  fontFamily: 'Open Sans',
  fontStyle: 'normal' as const,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '150%',
  letterSpacing: '0.01em',
  _hover: { borderColor: '#A8B8C2' },
  _focusVisible: {
    borderColor: '#00AAFF',
    boxShadow: '0 0 0 1px #00AAFF',
  },
};

function useLabelPadding(labelRef: React.RefObject<HTMLDivElement>, label: string) {
  const [paddingFull, setPaddingFull] = useState(140);
  const [paddingLabelOnly, setPaddingLabelOnly] = useState(96);

  useEffect(() => {
    if (!labelRef.current) return;

    const full = labelRef.current.getBoundingClientRect().width;
    setPaddingFull(full + 12);

    const labelTextEl = labelRef.current.firstElementChild;
    if (labelTextEl) {
      const labelOnly = labelTextEl.getBoundingClientRect().width;
      setPaddingLabelOnly(12 + labelOnly + 12); // left:12 + text + gap:12
    }
  }, [label]);

  return { paddingFull, paddingLabelOnly };
}

const InlineInputLabel: React.FC<{
  label: string;
  requiredText?: string;
  showRequired?: boolean;
  isRequiredVisible?: boolean;
  labelRef?: React.RefObject<HTMLDivElement>;
}> = ({ label, requiredText, showRequired = true, isRequiredVisible = true, labelRef }) => (
  <Flex
    ref={labelRef}
    position="absolute"
    left="12px"
    top="50%"
    transform="translateY(-50%)"
    align="center"
    gap="12px"
    pointerEvents="none"
    zIndex={1}
  >
    <Text fontWeight={600} fontSize="14px" color="#0A0A25" whiteSpace="nowrap">
      {label}
    </Text>
    {showRequired && (
      <Text
        fontWeight={400}
        fontSize="14px"
        color="#4F6470"
        whiteSpace="nowrap"
        opacity={isRequiredVisible ? 1 : 0}
        transition="opacity 0.15s"
      >
        {requiredText}
      </Text>
    )}
  </Flex>
);

const TextField: React.FC<BaseFieldProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  showRequired = true,
  requiredText,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const labelRef = useRef<HTMLDivElement>(null);
  const { paddingFull, paddingLabelOnly } = useLabelPadding(labelRef, label);

  const isRequiredVisible = showRequired && !isFocused && !value;

  return (
    <FormControl isRequired={showRequired} position="relative" bg="#FFFFFF" borderRadius="3px">
      <InlineInputLabel
        label={label}
        requiredText={requiredText}
        showRequired={showRequired}
        isRequiredVisible={isRequiredVisible}
        labelRef={labelRef}
      />
      <Input
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        type={type}
        aria-label={label}
        pl={`${isRequiredVisible ? paddingFull : paddingLabelOnly}px`}
        {...inputBaseStyles}
      />
    </FormControl>
  );
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  value,
  onChange,
  type = 'password',
  isVisible,
  onToggleVisibility,
  helperText,
  showRequired = true,
  requiredText,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const labelRef = useRef<HTMLDivElement>(null);
  const { paddingFull, paddingLabelOnly } = useLabelPadding(labelRef, label);

  const isRequiredVisible = showRequired && !isFocused && !value;

  return (
    <FormControl isRequired={showRequired}>
      <Stack spacing="4px">
        <Box position="relative" bg="#FFFFFF" borderRadius="3px">
          <InlineInputLabel
            label={label}
            requiredText={requiredText}
            showRequired={showRequired}
            isRequiredVisible={isRequiredVisible}
            labelRef={labelRef}
          />
          <InputGroup>
            <Input
              value={value}
              onChange={onChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              type={isVisible ? 'text' : type}
              aria-label={label}
              pl={`${isRequiredVisible ? paddingFull : paddingLabelOnly}px`}
              pr="44px"
              h="44px"
              {...inputBaseStyles}
            />
            <InputRightElement h="44px" w="44px">
              <IconButton
                aria-label={isVisible ? '隱藏密碼' : '顯示密碼'}
                icon={<Image src={isVisible ? visibleIcon : invisibleIcon} alt="" boxSize="24px" />}
                variant="ghost"
                size="sm"
                onClick={onToggleVisibility}
                minW="24px"
                h="24px"
                p="0"
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                _focusVisible={{ boxShadow: 'none' }}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
        {helperText && (
          <Text fontFamily="Open Sans" fontSize="14px" lineHeight="150%" letterSpacing="0.01em" color="#242B84">
            {helperText}
          </Text>
        )}
      </Stack>
    </FormControl>
  );
};

const RegisterPage: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<RegisterFormData>(INITIAL_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

  const handleChange = (field: keyof RegisterFormData) => (event: ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));

  const isPasswordValid = useMemo(() => PASSWORD_RULE.test(formData.password.trim()), [formData.password]);

  const isConfirmPasswordValid = useMemo(
    () => formData.confirmPassword.trim().length > 0 && formData.password === formData.confirmPassword,
    [formData.password, formData.confirmPassword],
  );

  // const isAllFilled = useMemo(() => Object.values(formData).every((v) => v.trim().length > 0), [formData]);
  const isAllFilled = useMemo(
    () => REQUIRED_FIELDS.every((field) => formData[field].trim().length > 0),
    [formData],
  );

  const isSubmitDisabled = !isAllFilled || !isPasswordValid || !isConfirmPasswordValid;

  const handleSubmit = (event: FormEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) return;
    console.log('register form data', formData);
  };

  const requiredText = t('register.required');
  const f = (key: string) => t(`register.fields.${key}`);

  return (
    <Box as="main" w="100%" bg="#FFFFFF">
      <Flex justify="center" px="24px">
        <Stack as="form" onSubmit={handleSubmit} w="100%" maxW="740px" spacing="0" pb="100px">
          {/* Header */}
          <Stack spacing="8px" align="center" pt="50px" pb="50px">
            <Text
              fontFamily="Open Sans"
              fontWeight={600}
              fontSize="30px"
              lineHeight="133%"
              letterSpacing="0.01em"
              color="#000000"
              textAlign="center"
            >
              {t('register.title')}
            </Text>
            <Text
              fontFamily="Open Sans"
              fontWeight={600}
              fontSize="20px"
              lineHeight="120%"
              letterSpacing="0.01em"
              color="#000000"
              textAlign="center"
            >
              {t('register.subtitle')}
            </Text>
          </Stack>

          {/* Fields */}
          <Stack spacing="30px" w="100%">
            <TextField
              label={f('organizationFullNameZh')}
              requiredText={requiredText}
              value={formData.organizationFullNameZh}
              onChange={handleChange('organizationFullNameZh')}
            />
            <TextField
              label={f('organizationCode')}
              requiredText={requiredText}
              value={formData.organizationCode}
              onChange={handleChange('organizationCode')}
            />

            <Flex gap="40px" align="flex-start" direction={{ base: 'column', md: 'row' }}>
              <Box flex="1" w="100%">
                <PasswordField
                  label={f('password')}
                  requiredText={requiredText}
                  value={formData.password}
                  onChange={handleChange('password')}
                  isVisible={showPassword}
                  onToggleVisibility={() => setShowPassword((p) => !p)}
                  helperText={t('register.passwordHint')}
                />
              </Box>
              <Box flex="1" w="100%">
                <PasswordField
                  label={f('confirmPassword')}
                  requiredText={requiredText}
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  isVisible={showConfirmPassword}
                  onToggleVisibility={() => setShowConfirmPassword((p) => !p)}
                />
              </Box>
            </Flex>

            <TextField
              label={f('organizationShortNameZh')}
              requiredText={requiredText}
              value={formData.organizationShortNameZh}
              onChange={handleChange('organizationShortNameZh')}
            />
            <TextField
              label={f('organizationShortNameEn')}
              requiredText={requiredText}
              value={formData.organizationShortNameEn}
              onChange={handleChange('organizationShortNameEn')}
            />
            <TextField
              label={f('contactName')}
              requiredText={requiredText}
              value={formData.contactName}
              onChange={handleChange('contactName')}
            />

            <Flex gap="40px" direction={{ base: 'column', md: 'row' }}>
              <Box flex="1" w="100%">
                <TextField
                  label={f('contactDepartment')}
                  requiredText={requiredText}
                  value={formData.contactDepartment}
                  onChange={handleChange('contactDepartment')}
                />
              </Box>
              <Box flex="1" w="100%">
                <TextField
                  label={f('contactTitle')}
                  requiredText={requiredText}
                  value={formData.contactTitle}
                  onChange={handleChange('contactTitle')}
                />
              </Box>
            </Flex>

            <Flex gap="40px" direction={{ base: 'column', md: 'row' }}>
              <Box flex="1" w="100%">
                <TextField
                  label={f('contactPhone')}
                  requiredText={requiredText}
                  type="tel"
                  value={formData.contactPhone}
                  onChange={handleChange('contactPhone')}
                />
              </Box>
              <Box flex="1" w="100%">
                <TextField
                  label={f('contactExtension')}
                  requiredText={requiredText}
                  type="tel"
                  value={formData.contactExtension}
                  onChange={handleChange('contactExtension')}
                  showRequired={false}
                />
              </Box>
            </Flex>

            <TextField
              label={f('contactEmail')}
              requiredText={requiredText}
              type="email"
              value={formData.contactEmail}
              onChange={handleChange('contactEmail')}
            />
          </Stack>

          {/* Actions */}
          <Flex justify="center" gap="20px" pt="50px">
            <Button
              type="button"
              minW="53px"
              h="37px"
              px="12px"
              border="1px solid #00AAFF"
              borderRadius="4px"
              bg="transparent"
              color="#00AAFF"
              fontFamily="Open Sans"
              fontSize="14px"
              fontWeight={400}
              lineHeight="150%"
              letterSpacing="0.01em"
              onClick={() => setIsLeaveModalOpen(true)}
              _hover={{ bg: '#F5FCFF' }}
              _active={{ bg: '#EAF8FF' }}
            >
              {t('register.actions.back')}
            </Button>
            <Button
              type="submit"
              minW="53px"
              h="37px"
              px="12px"
              borderRadius="4px"
              bg={isSubmitDisabled ? '#E6F7FF' : '#00AAFF'}
              color="#FFFFFF"
              isDisabled={isSubmitDisabled}
              aria-label={t('register.actions.submit')}
              fontFamily="Open Sans"
              fontSize="14px"
              fontWeight={400}
              lineHeight="150%"
              letterSpacing="0.01em"
              _hover={{ bg: isSubmitDisabled ? '#E6F7FF' : '#0095E0' }}
              _active={{ bg: isSubmitDisabled ? '#E6F7FF' : '#0087CC' }}
              _disabled={{ opacity: 1, cursor: 'not-allowed', bg: '#E6F7FF', color: '#FFFFFF' }}
            >
              {t('register.actions.submit')}
            </Button>
          </Flex>
        </Stack>
      </Flex>

      {/* Leave Modal */}
      <Modal isOpen={isLeaveModalOpen} onClose={() => setIsLeaveModalOpen(false)} isCentered>
        <ModalOverlay bg="rgba(37, 42, 49, 0.12)" />
        <ModalContent
          w="540px"
          maxW="540px"
          h="125px"
          bg="#FFFFFF"
          boxShadow="0px 12px 24px rgba(37, 42, 49, 0.24), 0px 8px 60px rgba(37, 42, 49, 0.32)"
          borderRadius="12px"
          p="24px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="16px"
        >
          <Text
            fontFamily="Open Sans"
            fontWeight={600}
            fontSize="20px"
            lineHeight="120%"
            letterSpacing="0.01em"
            color="#000000"
          >
            {t('register.leaveDialog.title')}
          </Text>
          <Flex justify="flex-end" align="center" gap="8px" w="100%">
            <Button
              type="button"
              h="37px"
              px="12px"
              border="1px solid #D21C1B"
              borderRadius="4px"
              bg="#FFFFFF"
              color="#D21C1B"
              fontFamily="Open Sans"
              fontSize="14px"
              fontWeight={400}
              lineHeight="150%"
              letterSpacing="0.01em"
              onClick={() => setIsLeaveModalOpen(false)}
              _hover={{ bg: '#FFF5F5' }}
              _active={{ bg: '#FDECEC' }}
            >
              {t('register.leaveDialog.stay')}
            </Button>
            <Button
              type="button"
              h="37px"
              px="12px"
              bg="#D21C1B"
              borderRadius="4px"
              color="#FFFFFF"
              fontFamily="Open Sans"
              fontSize="14px"
              fontWeight={400}
              lineHeight="150%"
              letterSpacing="0.01em"
              onClick={() => {
                setIsLeaveModalOpen(false);
                history.push('/vc-verification');
              }}
              _hover={{ bg: '#B91818' }}
              _active={{ bg: '#A11616' }}
            >
              {t('register.leaveDialog.leave')}
            </Button>
          </Flex>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RegisterPage;
